import {
  getIsExpirationDateEqualToCheckIn,
  getTotalPriceText,
  Icon,
  IconName,
  twoDecimalFormatter,
  useDeviceTypes,
} from "halifax";
import { Box, Typography } from "@material-ui/core";
import React, { useMemo } from "react";
import clsx from "clsx";
import { RouteComponentProps } from "react-router-dom";
import { getConfigTenant, getPortalName, isCorpTenant } from "@capone/common";
import dayjs from "dayjs";
import type { FiatPrice } from "@b2bportal/purchase-api";
import { CfarCancellationPolicyConnectorProps } from "./container";

const CancellationPolicyCopyV2: React.FC<{
  portalName: string;
  formattedExpirationDate: string;
  expirationEqualToCheckIn: boolean;
  coveragePercentage: number;
  formattedCoverageAmount: string;
  isFullyRefundable: boolean;
}> = ({
  portalName,
  formattedExpirationDate,
  expirationEqualToCheckIn,
  coveragePercentage,
  formattedCoverageAmount,
  isFullyRefundable,
}) => {
  const lastSentence = useMemo(() => {
    if (coveragePercentage < 100) {
      return "Cancellations made after this period are nonrefundable.";
    }

    return `Cancellations made after this period are subject to a penalty according to the hotel's policy. Cancel for any reason fee is${
      isFullyRefundable ? " " : " not "
    }included in the refund.`;
  }, [coveragePercentage, isFullyRefundable]);

  return (
    <Typography variant="inherit" component="span">
      Your room is made refundable by {portalName}. Cancel for any reason up
      until {formattedExpirationDate}
      {expirationEqualToCheckIn ? " (check-in time)" : ""} and get a{" "}
      <strong>refund of {formattedCoverageAmount}</strong>. {lastSentence}
    </Typography>
  );
};

const CancellationPolicyCopy: React.FC<{
  isFullyRefundable: boolean;
  portalName: string;
  formattedCoverageAmount: string;
}> = ({ isFullyRefundable, portalName, formattedCoverageAmount }) => {
  if (isFullyRefundable) {
    return (
      <Typography variant="inherit" component="span">
        Your room is made refundable by {portalName}. Cancel for any reason up
        until your scheduled check-in time and get{" "}
        <strong>
          a full refund of your hotel base rate plus taxes and fees (
          {formattedCoverageAmount})
        </strong>
        . Cancellations made after this period are subject to a penalty
        according to the hotel's policy. Cancel for any reason fee will be
        included in the refund.
      </Typography>
    );
  }

  return (
    <Typography variant="inherit" component="span">
      Your room is made refundable by Capital One Travel. Cancel for any reason
      up until your scheduled check-in time and get{" "}
      <strong>
        a refund of your hotel base rate plus taxes ({formattedCoverageAmount})
      </strong>
      . Cancellations made after this period are subject to a penalty according
      to the hotel's policy. Cancel for any reason fee will not be included in
      the refund.
    </Typography>
  );
};

export interface ICfarCancellationPolicyProps
  extends RouteComponentProps,
    CfarCancellationPolicyConnectorProps {}

export const CfarCancellationPolicy = (props: ICfarCancellationPolicyProps) => {
  const { hotelCfarQuote, hotelCfarQuoteV2, checkInDate, checkInTime } = props;

  if (!hotelCfarQuote && !hotelCfarQuoteV2) {
    return null;
  }

  const tenant = getConfigTenant();

  const isCfarFeeRefundable =
    hotelCfarQuoteV2?.refundPremiumAtExercise === true;

  const premium =
    hotelCfarQuoteV2?.premiumAmount ?? hotelCfarQuote?.premiumPrices;

  // One of hotelCfarQuote or hotelCfarQuoteV2 is defined at this point, so we can safely cast to string
  const currencySymbol = premium?.fiat.currencySymbol as string;

  // One of hotelCfarQuote or hotelCfarQuoteV2 is defined at this point, so we can safely use non-null assertion on the fallback case of hotelCfarQuote
  const coverage: FiatPrice | undefined = useMemo(() => {
    if (hotelCfarQuoteV2?.coverageAmount) {
      return hotelCfarQuoteV2.coverageAmount.fiat;
    }
    return {
      value: hotelCfarQuote!.coverageAmount.amount,
      currencySymbol,
      currencyCode: hotelCfarQuote!.coverageAmount.currency,
    };
  }, [
    hotelCfarQuoteV2?.coverageAmount,
    hotelCfarQuote?.coverageOverriding?.overridingCoverage,
    hotelCfarQuote?.coverageAmount,
    currencySymbol,
  ]);

  const formattedCheckInDate = useMemo(() => {
    if (!checkInDate) {
      return undefined;
    }
    return dayjs(checkInDate).startOf("day").format("YYYY-MM-DD");
  }, [checkInDate]);

  const { matchesMobile } = useDeviceTypes();

  const hideIcon = isCorpTenant(tenant) && matchesMobile;
  const portalName = getPortalName();

  return (
    <Box
      className={clsx(
        "checkout-template-card-content-container",
        "cancellation-details"
      )}
    >
      <Typography variant="h4" component="h2">
        {!hideIcon && <Icon name={IconName.CheckShieldBlue} />}
        <span
          className={clsx("cancellation-details-title", {
            padding: isCorpTenant(tenant),
          })}
        >
          Cancel for any reason
        </span>
      </Typography>
      <Typography variant="body2">
        {hotelCfarQuoteV2 ? (
          <CancellationPolicyCopyV2
            portalName={portalName}
            formattedExpirationDate={dayjs(
              hotelCfarQuoteV2.expirationInHotelTz
            ).format("MMMM D, YYYY [at] h:mm A")}
            expirationEqualToCheckIn={getIsExpirationDateEqualToCheckIn(
              formattedCheckInDate,
              checkInTime,
              hotelCfarQuoteV2.expirationInHotelTz
            )}
            coveragePercentage={hotelCfarQuoteV2.coveragePercentage}
            formattedCoverageAmount={getTotalPriceText({
              price: hotelCfarQuoteV2.coverageAmount.fiat,
              priceFormatter: twoDecimalFormatter,
            })}
            isFullyRefundable={isCfarFeeRefundable}
          />
        ) : (
          <CancellationPolicyCopy
            isFullyRefundable={isCfarFeeRefundable}
            portalName={portalName}
            formattedCoverageAmount={getTotalPriceText({
              price: coverage,
              priceFormatter: twoDecimalFormatter,
            })}
          />
        )}
      </Typography>
    </Box>
  );
};
